/**
 * @class ProductCrossSell
 * @classdesc This component fetches content for the cross-sell feature
 *
 * @extends Core.Component
 */

export default class ProductCrossSell extends Core.Component {
  onMount() {
    this.nextButton = null;
    this.prevButton = null;
  }

  onDismount() {
    this.unload();
  }

  /**
   * Fetches section content via the Shopify recommendations endpoint
   * @param {string} sectionId - the ID of the section to fetch
   */
  fetchRecommendations(sectionId) {
    const url = `${this.data.recommendations_route}?section_id=${sectionId}&product_id=${this.data.product_id}&limit=4`;
    return fetch(url)
      .then(response => response.text())
      .then(text => {
        const html = document.createElement('div');
        html.innerHTML = text;

        this.el.querySelector('[data-cross-sell-content]').innerHTML = html.querySelector('[data-cross-sell-content]').innerHTML;
        this._initAnimations();
        this._bindEvents();
      });
  }

  /**
   * Initialize the animations for switching cards
   */
  _initAnimations() {
    this.cardWrappers = this.el.querySelectorAll('[data-cross-sell-card-wrapper]');
    this.animations = {};
    this.animatingIn = false;
    this.animatingOut = false;

    this.cardWrappers.forEach(el => {
      const { cardIndex } = el.dataset;
      let state = 'hidden';

      if (parseInt(cardIndex) === 1) {
        state = 'visible';
      }

      this.animations[cardIndex] = Core.Animations.transition({ el, state });
    });
  }

  /**
   * Initialize animations
   * Bind click events to previous and next buttons
   */
  _bindEvents() {
    this.nextButton = this.el.querySelector('[data-cross-sell-next]');
    this.prevButton = this.el.querySelector('[data-cross-sell-previous]');

    if (this.nextButton && this.prevButton) {
      this.events.register(this.nextButton, 'click', e => this._nextProduct(e.currentTarget));
      this.events.register(this.prevButton, 'click', e => this._previousProduct(e.currentTarget));
    }
  }

  /**
   * Callback for 'next' button click
   * @param target {element} - the click event's 'currentTarget'
   */
  _nextProduct(target) {
    if (this.animatingIn || this.animatingOut || target.hasAttribute('disabled')) return;

    const selectedEvent = new CustomEvent('cross-sell:selected');
    const selectedCard = this.el.querySelector('[data-cross-sell-selected]');
    const selectedIndex = parseInt(selectedCard.dataset.cardIndex);
    const nextIndex = selectedIndex + 1;
    const nextCard = this.el.querySelector(`[data-card-index="${nextIndex}"]`);
    const navLabel = this.el.querySelector('[data-index-current]');


    if (this.animations[selectedIndex] && this.animations[nextIndex]) {
      this.animatingIn = true;
      this.animatingOut = true;

      if (nextIndex <= this.cardWrappers.length) {
        this.animations[selectedIndex].animateTo('hidden')
          .then(() => {
            this.animatingOut = false;
          });
        this.animations[nextIndex].animateTo('visible')
          .then(() => {
            this.animatingIn = false;
          });

        selectedCard.removeAttribute('data-cross-sell-selected');
        nextCard.setAttribute('data-cross-sell-selected', '');

        const cardComponent = nextCard.querySelector('[data-component="cross-sell-card"]');

        cardComponent.dispatchEvent(selectedEvent);

        navLabel.innerText = nextIndex;

        if (nextIndex === this.cardWrappers.length) {
          this.nextButton.setAttribute('disabled', '');
        }

        if (selectedIndex === 1) {
          this.prevButton.removeAttribute('disabled');
        }
      }
    }
  }

  /**
   * Callback for 'previous' button click
   * @param target {element} - the click event's 'currentTarget'
   */
  _previousProduct(target) {
    if (this.animatingIn || this.animatingOut || target.hasAttribute('disabled')) return;

    const selectedCard = this.el.querySelector('[data-cross-sell-selected]');
    const selectedIndex = parseInt(selectedCard.dataset.cardIndex);
    const nextIndex = selectedIndex - 1;
    const nextCard = this.el.querySelector(`[data-card-index="${nextIndex}"]`);
    const navLabel = this.el.querySelector('[data-index-current]');

    if (this.animations[selectedIndex] && this.animations[nextIndex]) {
      this.animatingIn = true;
      this.animatingOut = true;

      if (nextIndex >= 1) {
        this.animations[selectedIndex].animateTo('hidden')
          .then(() => {
            this.animatingOut = false;
          });

        this.animations[nextIndex].animateTo('visible')
          .then(() => {
            this.animatingIn = false;
          });

        selectedCard.removeAttribute('data-cross-sell-selected');
        nextCard.setAttribute('data-cross-sell-selected', '');

        navLabel.innerText = nextIndex;

        if (nextIndex === 1) {
          this.prevButton.setAttribute('disabled', '');
        }

        if (selectedIndex === this.cardWrappers.length) {
          this.nextButton.removeAttribute('disabled');
        }
      }
    }
  }

  unload() {
    this.events.unregisterAll();
    this.animations = {};
  }
}
